import { useState } from "react";
import { Clock, Filter, Sparkles, Send, Play, Save, ArrowRight } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, GhostButton } from "@/components/web/atoms";
import { Reveal, BrowserFrame, trackHomeEvent, scrollToId } from "@/components/web/home/primitives";

/* Section 8 — Automation. Plain-English → a reviewable workflow. */
const STEPS = [
  { tag: "WHEN", icon: Clock, text: "Monday at 8:00 AM", tone: "brand" },
  { tag: "FIND", icon: Filter, text: "Overdue tasks", tone: "brand" },
  { tag: "THEN", icon: Sparkles, text: "Summarize with AI", tone: "ai" },
  { tag: "THEN", icon: Send, text: "Post to Operations", tone: "brand" },
];

export default function AutomationSection() {
  const [typed] = useState(
    "Every Monday, summarize overdue tasks and post the report in Operations.",
  );
  return (
    <section
      id="automation"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-automation"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">Automation</Eyebrow>
          <SectionTitle>Tell TeamNest what you want done repeatedly.</SectionTitle>
          <SectionSub className="mt-5">
            Describe an automation in plain English. TeamNest turns it into a workflow you can review,
            test and save.
          </SectionSub>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Reveal>
            <BrowserFrame url="teamnest.ai/automations">
              <div className="p-5 sm:p-6 bg-[var(--w-bg)]">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--w-text-mute)] mb-2">You type</div>
                <div className="rounded-[12px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-3 text-[14px] leading-6 text-[var(--w-text)]">
                  “{typed}”
                </div>
                <div className="mt-3 text-[12px] text-[var(--w-text-mute)] italic">No workflow diagram required to get started.</div>
              </div>
            </BrowserFrame>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-[16px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--w-text-mute)] mb-4">TeamNest builds</div>
              <div className="space-y-2.5">
                {STEPS.map((s, i) => {
                  const accent = s.tone === "ai" ? "var(--w-ai)" : "var(--w-brand)";
                  const tint = s.tone === "ai" ? "var(--w-ai-tint)" : "var(--w-brand-tint)";
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-[12px] border border-[var(--w-hairline)] bg-[var(--w-bg2)] p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider w-11 shrink-0" style={{ color: accent }}>{s.tag}</span>
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: tint }}>
                        <s.icon className="w-4 h-4" style={{ color: accent }} />
                      </span>
                      <span className="text-[14px] text-[var(--w-text)]">{s.text}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[var(--w-hairline)] bg-[var(--w-bg2)] text-[12.5px] font-medium text-[var(--w-text-dim)]">
                  <Play className="w-3.5 h-3.5" /> Run Test
                </span>
                <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] bg-[var(--w-brand)] text-black text-[12.5px] font-semibold">
                  <Save className="w-3.5 h-3.5" /> Save Automation
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={200} className="mt-8">
          <GhostButton
            as="button"
            type="button"
            data-testid="automation-cta"
            onClick={() => { trackHomeEvent("automation-cta", { source: "automation" }); scrollToId("how-it-works"); }}
          >
            Explore Automations <ArrowRight className="w-4 h-4" />
          </GhostButton>
        </Reveal>
      </div>
    </section>
  );
}
